import logoUrl from '../../assets/Menusso_logo.svg';
import './LoginPage.scss';
import { useTranslation } from 'react-i18next';
import Input from '../../components/input/Input';
import { Button } from '@mui/material';
import { useLogin, useNotify } from 'react-admin';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { MemberService } from '../../services/member/memberService';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../../core/firebaseConfig';
import * as Sentry from '@sentry/react';
import LanguageSwitcher from '../../components/language-switcher/LanguageSwitcher';

export default function LoginPage() {
  const login = useLogin();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const memberService = new MemberService();

  const validationSchema = Yup.object({
    email: Yup.string().email(t('validation.emailInvalid')).required(t('validation.required')),
    password: Yup.string().required(t('validation.required')),
  });

  return (
    <Formik
      initialValues={{ email: '', password: '' }}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange
      onSubmit={async (values, { setSubmitting }) => {
        setLoading(true);
        try {
          const userCredential = await signInWithEmailAndPassword(
            auth,
            values.email,
            values.password,
          );
          const { user } = userCredential;
          const token = await user.getIdToken();
          try {
            const response = await memberService.signIn(userCredential.user.uid);
            if (
              response?.isBlocked ||
              response?.isDeleted ||
              !response?.roles?.some(
                (role: string) =>
                  role === 'ADMIN' ||
                  role === 'SUPER_ADMIN' ||
                  role === 'VENDOR' ||
                  role === 'RESTAURANT_OWNER',
              )
            ) {
              // Odrzucone logowanie nie moze zostawic zywej sesji Firebase.
              await signOut(auth);
              notify(t('notifications.loginError'), { type: 'error' });
              return;
            }
            localStorage.setItem('ra_role', response?.roles);
            Sentry.setUser({
              id: userCredential.user.uid,
              email: userCredential.user.email ?? undefined,
              role: response?.roles,
            });
            await login({ uid: userCredential.user.uid, token }, '/');
            window.location.reload();
          } catch (e: any) {
            console.error(e);
            // 403 = konto klienta (backend wpuszcza do panelu tylko pracownikow).
            if (e?.response?.status !== 403) {
              Sentry.captureException(e);
            }
            await signOut(auth).catch(() => undefined);
            notify(t('notifications.loginError'), { type: 'error' });
          }
        } catch (e: any) {
          console.error(e);
          const message =
            e?.code === 'auth/invalid-credential'
              ? t('login.invalidCredential')
              : t('login.failed');
          notify(message, { type: 'error' });
          Sentry.captureException(e);
        } finally {
          setLoading(false);
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <Form className="login login--centered">
          <div className="login__language">
            <LanguageSwitcher />
          </div>
          <div className="login__card login__card--elevated">
            <img className="login__logo" src={logoUrl} alt="Menusso logo" />
            <div>
              <p className="login__title">{t('login.logInToYourAccount')}</p>
              <p className="login__subtitle">{t('login.welcomeBack')}</p>
            </div>
            <div className="login__form">
              <Input
                name="email"
                label={t('login.email')}
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                autoComplete="email"
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email ? errors.email : undefined}
                aria-invalid={Boolean(touched.email && errors.email)}
                aria-describedby="email-error"
              />
              <Input
                name="password"
                label={t('login.password')}
                onChange={handleChange}
                onBlur={handleBlur}
                type="password"
                value={values.password}
                autoComplete="current-password"
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password ? errors.password : undefined}
                aria-invalid={Boolean(touched.password && errors.password)}
                aria-describedby="password-error"
              />
              <Button
                className="login__submit"
                variant="contained"
                fullWidth
                type="submit"
                disabled={isSubmitting || loading}
              >
                {t('login.logIn')}
              </Button>
              <Link to="/forgot-password" className="login__forgot-password">
                {t('login.forgotPassword')}
              </Link>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
